import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import report from "@report";
import { PAGES } from "@routes";
import { useMembershipProducts, useSelectCountryKey } from "@src/jotai/pricing";
import { selectRegionName } from "@store/globalSlice";
import {
  IND_PRODUCT,
  MemberFeatureConfig,
  PRICING_LEVEL,
  updateRedirectURL,
  updateSubscribeProducts,
} from "@store/memberCenterSlice";
import { isInIframe } from "@utils/window";
import { gasStation } from "./config";
import { getListCategoryResult, modifyMemberDynamicConfig, useCategoryMemberModule } from "./tempFeature";
import { MembershipProduct } from "./types";
import useBelongCharm from "@shared/global/useBelongCharm";
import { isMutipleRegion } from "./utils";

/**
 * 用户权限点跳转
 */
export const useNavigatePagePricing = (feature?: string) => {
  const navigate = useNavigate();
  return (reportData?: any) => {
    report.click({
      kalo_pos: !!reportData?.kalo_pos ? "GoPricing_" + reportData.kalo_pos : "GoPricing",
      kalo_value: feature,
    });
    if (isInIframe()) {
      window.parent.postMessage({ type: "NAVIGAE_PRICING" }, "*");
      return;
    }
    navigate(PAGES.PRICING);
  };
};

export interface MemberFeatue {
  showMask: boolean;
  count: number;
  expired: boolean;
  action: string;
  desc: string;
  level: PRICING_LEVEL;
}

const DETAILMODULES = [
  "SHOP.DETAIL",
  "PRODUCT.DETAIL",
  "VIDEO.DETAIL",
  "LIVESTREAM.DETAIL",
  "CREATOR.DETAIL",
  "OVERVIEW.DETAIL",
];
export const getMemberFeature = (dynamicConfig: any, module: any, membership: any) => {
  if (module && dynamicConfig && dynamicConfig[module]) {
    let showMask = false;
    const featureConfig = dynamicConfig[module] as MemberFeatureConfig;
    const detailAccessTimes = dynamicConfig["DETAIL.ACCESS_TIMES"];
    if (featureConfig) {
      switch (featureConfig.type) {
        case "MODULE": {
          if (featureConfig.count === 0) {
            showMask = true;
          }
          break;
        }
        case "LIST": {
          if (featureConfig.count && featureConfig.count > 0) {
            showMask = true;
          }
          break;
        }
        case "ASSETS": {
          if (featureConfig.progress && featureConfig.progress.total !== -1 && featureConfig.progress.remain <= 0) {
            showMask = true;
          }
          break;
        }
        case "BENEFIT": {
          if (
            featureConfig.count &&
            featureConfig.count > 0 &&
            featureConfig.progress &&
            featureConfig.progress?.remain <= 0
          ) {
            showMask = true;
          }
          break;
        }
      }
      let desc = (featureConfig as any)?.guidance?.actionType?.desc || "";
      if (module === "FILTER.CUSTOM_TIMES") desc = (featureConfig as any)?.guidance?.message || "";
      // 详情页次数用尽， 详情模块展示遮罩
      if (
        detailAccessTimes.count &&
        detailAccessTimes.count > 0 &&
        detailAccessTimes.progress?.remain <= 0 &&
        DETAILMODULES.includes(module)
      ) {
        showMask = true;
      }
      return {
        action: featureConfig?.guidance?.actionType?.name || "",
        desc: desc,
        showMask,
        count: featureConfig.count,
        level: membership?.level,
        expired: membership?.expired,
      };
    }
  }
  return null;
};

type UseMemberFeatureFunction = {
  (module?: string): MemberFeatue | null;
  (module: string[]): (MemberFeatue | null)[];
};

export const useMemberFeature: UseMemberFeatureFunction = (module?: string | string[]) => {
  const { region, membership } = useAppSelector((state) => state.globalReducer);
  const { dynamicConfig } = useAppSelector((state) => state.memberCenterReducer);
  const newDynamicConfig = modifyMemberDynamicConfig(region, dynamicConfig, membership as any);
  const result = getListCategoryResult(region, module, membership);
  if (result) return result as any;

  if (Array.isArray(module)) {
    return module.map((feature) => getMemberFeature(newDynamicConfig, feature, membership)) as any;
  } else {
    return getMemberFeature(newDynamicConfig, module, membership!);
  }
};

export const useGasStation = () => {
  const dispatch = useAppDispatch();
  const region = "US";
  const { indProducts: _indProducts } = useAppSelector((state) => state.memberCenterReducer);

  // useEffect(() => {
  //   if (indProducts.length === 0) {
  //     dispatch(fetchIndProducts());
  //   }
  // }, []);

  const addGasStationToShoppingCart = (product: IND_PRODUCT, redirectURL: string = "") => {
    dispatch(
      updateSubscribeProducts([
        {
          type: "INDEPENDENT",
          region: region,
          key: product.key,
          name: gasStation[product.key].title,
          mark: gasStation[product.key].mark,
          unit: gasStation[product.key].unit,
          realprice: product.price,
          itemId: product.id,
          quantity: 1,
        },
      ]),
    );
    dispatch(updateRedirectURL(redirectURL));
  };

  return { addGasStationToShoppingCart };
};

// 是否可以搜索类目，基础二级三级类目
export const useHasCategoryPermission = () => {
  const memberModule = useCategoryMemberModule();
  const memberFeature = useMemberFeature(memberModule);
  return { hasCategoryPermission: memberFeature?.showMask !== true, memberFeature };
};

export const useCurrentProduct = () => {
  const { membershipProducts } = useMembershipProducts();
  const { duration } = useAppSelector((state) => state.memberCenterReducer);
  const { selectCountryKey } = useSelectCountryKey();
  const regionName = useSelector(selectRegionName);
  let productRegionName = isMutipleRegion(selectCountryKey) ? selectCountryKey : regionName;

  const { selectedProducts } = useAppSelector((state) => state.memberCenterReducer);

  const getCurrentProduct = (level: string): MembershipProduct | undefined => {
    //@ts-ignore
    return membershipProducts?.[productRegionName]?.[duration]?.[level]?.[0];
  };
  const currentProduct = getCurrentProduct(selectedProducts?.[0]?.level);
  return { currentProduct, getCurrentProduct };
};

export const usePeopleSuggestion = () => {
  const { isBelongCharm } = useBelongCharm();

  const MEMBER_PEOPLE_SUGGESTION = ["", "For individuals", "For small teams", "For enterprise"];
  const MEMBER_PEOPLE_SUGGESTION_MAP: { [key: string]: string } = {
    Basic: "For individuals",
    Standard: "For small teams",
    Premium: "For enterprise",
  };

  const displaySuggestionByIndex = (index: number) => {
    if (!isBelongCharm && index === 2) {
      // 非Charm 标准套餐 隐藏人数
      return MEMBER_PEOPLE_SUGGESTION[index]?.replace(/\(.*\)/, "");
    }
    return MEMBER_PEOPLE_SUGGESTION[index];
  };
  const displaySuggestionByLevel = (level: string) => {
    if (!isBelongCharm && level === "Standard") {
      // 非Charm 标准套餐 隐藏人数
      return MEMBER_PEOPLE_SUGGESTION_MAP[level]?.replace(/\(.*\)/, "");
    }
    return MEMBER_PEOPLE_SUGGESTION_MAP[level];
  };
  return { displaySuggestionByIndex, displaySuggestionByLevel };
};

export const useAICredits = () => {
  const StandardAICredits = "70";
  return { StandardAICredits };
};

export const useMemberDiscount = () => {
  const { region } = useAppSelector((state) => state.globalReducer);
  const { selectCountryKey } = useSelectCountryKey();

  const regionList = ["US", "GB", "DE", "FR", "ES", "IT", "MX", "BR"];

  if (!isMutipleRegion(selectCountryKey)) {
    if (regionList.includes(region)) {
      return "20%";
    }
  }

  return "30%";
};

export const useShowMoney = (value: string | undefined) => {
  if (!value) return null;
  const firstUnitNonNumber = value?.[0]?.replace(/\d/g, "");

  return (
    <div className="flex items-baseline">
      <div className="text-[24px] text-[#222] font-bold leading-[48px]">{firstUnitNonNumber}</div>
      <div className="text-[38px] text-[#222] font-bold leading-[48px]">
        {value.replace(firstUnitNonNumber || "", "")}
      </div>
    </div>
  );
};
